import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import { enqueueSnackbar } from 'notistack';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import MapGL from '../../Map/Map';
import { useAppDispatch } from '../../../state/store';
import { setSensorsById } from '../../../state/actions';
import { MapCentrePosition, SpaceParams } from '../../Map/mapHelpers';
import TaggedSensorsList from './TaggedSensorsList';
import SensorsInLoc from './SensorsInLoc';
import LocationSwitcher from '../../../components/LocationSwitcher';
import useGlobalStyles from '../../../styles';
import useStyles from '../../../styles/tags';
import useSensorDetailsInLoc from './useSensorDetailsInLoc';
import TagNameForms from './TagNameForms';
import { themeProps } from '../../../styles/theme';
import { fetchSensorLatest, updateSensorProperties } from '../../../services/apiService';

interface TagConfigDialogProps {
  showDialog: boolean;
  closeDialog: () => void;
  selectedTag: string;
  setSelectedTag: (value: string) => void;
  allSpaces: SpaceParams[] | undefined;
}

function TagConfigDialog({
  showDialog,
  closeDialog,
  selectedTag,
  setSelectedTag,
  allSpaces,
}: TagConfigDialogProps) {
  const dispatch = useAppDispatch();
  const { classes: globalClasses } = useGlobalStyles();
  const { classes } = useStyles();
  const [taggedSensors, setTaggedSensors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sensorsDetailsInLoc = useSensorDetailsInLoc();

  const existingTaggedSensors = useMemo(
    () =>
      sensorsDetailsInLoc
        .filter((sensor) => sensor.tags?.some((item) => item === selectedTag))
        .map((item) => item.id),
    [selectedTag, sensorsDetailsInLoc]
  );

  // set tagged sensors from store for initial landing
  useEffect(() => {
    setTaggedSensors(existingTaggedSensors);
  }, [existingTaggedSensors]);

  // add or remove sensors locally
  const handleTagging = useCallback((sensorId: string) => {
    setTaggedSensors((prev) =>
      prev.includes(sensorId) ? prev.filter((id) => id !== sensorId) : [...prev, sensorId]
    );
  }, []);

  // order-independent comparison of two sensor id lists
  const areSensorListsEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const setA = new Set(a);
    return b.every((id) => setA.has(id));
  };

  // update tag on sensor's tags array; returns false if this sensor failed
  const updateSensorTag = useCallback(
    async (sensorId: string, action: 'add' | 'remove'): Promise<boolean> => {
      try {
        const sensor = await fetchSensorLatest(sensorId);
        if (!sensor) return false;

        const allTags = sensor.tags ?? [];
        const addTag = (tags: string[]) =>
          tags.includes(selectedTag) ? tags : [...tags, selectedTag];
        const newTags =
          action === 'add' ? addTag(allTags) : allTags.filter((tag) => tag !== selectedTag);
        const response = await updateSensorProperties(sensor.id, { tags: newTags });
        if (response?.id) {
          const updatedSensorDetails = { ...sensor, ...response };
          dispatch(setSensorsById([updatedSensorDetails]));
          return true;
        }
        return false;
      } catch (error) {
        enqueueSnackbar(
          `Failed to ${action} tag "${selectedTag}" on sensor ${sensorId}, ${error}`,
          { variant: 'error', persist: true }
        );
        return false;
      }
    },
    [dispatch, selectedTag]
  );

  // update tag form sensor props
  const handleSubmit = useCallback(async () => {
    if (!selectedTag) {
      enqueueSnackbar('Tag name is missing', { variant: 'error' });
      return;
    }

    if (areSensorListsEqual(existingTaggedSensors, taggedSensors)) {
      enqueueSnackbar('No changes detected', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);

    // sensors that had the tag before but no longer do
    const removedSensors = existingTaggedSensors.filter(
      (sensorId) => !taggedSensors.includes(sensorId)
    );
    // only sensors newly tagged in this session need an "add" call -
    // sensors that were already tagged and remain tagged don't need re-submitting
    const addedSensors = taggedSensors.filter(
      (sensorId) => !existingTaggedSensors.includes(sensorId)
    );

    // whether this tag had no sensors before this edit, used for the success message below
    const isNewTag = existingTaggedSensors.length === 0;

    const results = await Promise.all([
      ...removedSensors.map((sensorId) => updateSensorTag(sensorId, 'remove')),
      ...addedSensors.map((sensorId) => updateSensorTag(sensorId, 'add')),
    ]);

    setIsSubmitting(false);

    const failureCount = results.filter((succeeded) => !succeeded).length;

    if (failureCount > 0) {
      enqueueSnackbar(
        `Failed to update ${failureCount} sensor${failureCount > 1 ? 's' : ''}. Please try again.`,
        { variant: 'error' }
      );
      return;
    }

    closeDialog();
    setTaggedSensors([]);
    enqueueSnackbar(`Tag successfully ${isNewTag ? 'Created' : 'Updated'}`, {
      variant: 'success',
    });
  }, [closeDialog, existingTaggedSensors, selectedTag, taggedSensors, updateSensorTag]);

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      fullScreen
      PaperProps={{
        sx: {
          overflow: 'unset',
        },
      }}
      sx={{ padding: '2rem' }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
        }}
      >
        <MapGL
          mapCentrePosition={MapCentrePosition.right}
          taggedSensors={taggedSensors}
          handleTagging={handleTagging}
          processAllSpaces
        />
        <IconButton aria-label="close" onClick={closeDialog} className={classes.configCloseBtn}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ position: 'absolute', top: 10, left: 50, minWidth: '490px' }}>
          <LocationSwitcher />
        </Box>
        <Box className={classes.configContainer}>
          <Box className={globalClasses.compareListPanel}>
            <Box className={globalClasses.compareList} sx={{ borderRadius: '8px' }}>
              <Box className={globalClasses.spaceBetweenFlex}>
                <Typography variant="h5">Tag Config</Typography>
                <Box className={globalClasses.centeredFlex}>
                  <Button
                    size="small"
                    variant={themeProps.btnVariant.default}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                  <Button
                    size="small"
                    variant={themeProps.btnVariant.default}
                    onClick={closeDialog}
                    disabled={isSubmitting}
                    sx={{ marginLeft: '5px' }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
              <TagNameForms
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                existingTaggedSensors={existingTaggedSensors}
                allSpaces={allSpaces}
              />
              <TaggedSensorsList
                taggedSensors={taggedSensors} // WIP sensors that are being added/updated
                existingTaggedSensors={existingTaggedSensors} // sensors that already exists before any edit or update
                handleTagging={handleTagging}
              />
            </Box>
          </Box>

          {/*  blur out until tag space is selected */}
          <Box className={globalClasses.compareListPanel} sx={{ position: 'relative' }}>
            <Box
              className={globalClasses.compareList}
              sx={{
                borderRadius: '8px',
                opacity: selectedTag ? 1 : 0.4,
                filter: selectedTag ? 'none' : 'blur(1px)',
                boxShadow: selectedTag ? 'none' : 'inset 0 0 40px rgba(0,0,0,0.35)',
                transition: 'opacity 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease',
                pointerEvents: selectedTag ? 'auto' : 'none',
              }}
            >
              <SensorsInLoc
                taggedSensors={[
                  ...existingTaggedSensors,
                  ...taggedSensors.filter((item) => !existingTaggedSensors.includes(item)),
                ]}
                handleTagging={handleTagging}
              />
            </Box>

            {!selectedTag && (
              <Box className={classes.tagSelectAlertBox}>
                <Alert severity="info" sx={{ boxShadow: 3 }}>
                  Please select the tag space first to allow sensors selection
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default TagConfigDialog;
